import React from 'react';
import { Observable, EMPTY, of, concat, forkJoin } from 'rxjs';
import { mergeMap, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import get from 'lodash.get';

import { ModalService } from 'ui-components/services/modal-service';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';
import { DEFAULT_ORDERS_PAGE_URL } from '../../services/user-permission-service';
import { createLookupHash } from '../../services/hash-service';
import Global from '../../store/global-selectors';
import * as EntitiesActions from '../../store/entities/entities.actions';
import * as BatchesGridActions from '../../store/batches/grid/batches.grid.actions';
import * as BatchDetailsApi from '../../store/batches/details/batch.details.api';
import * as Actions from './batches.actions';
import * as NavActions from './nav/batches.nav.actions';
import { bulkloadFulfillmentPlans, bulkloadSalesOrders } from '../bulkload/bulkload.actions';

import * as Api from './batches.api';
import { BatchRejectedFulfillmentPlanModal } from '../../components/modals/batch-rejected-fulfillment-plan/';
import { messages } from './messages';

const showNotificationForOrderDetailsAddToBatch = ({ message, notificationType }) => {
  if (message) {
    NotificationService.showNotification({
      title: <FormattedMessage {...message.title} />,
      content: <FormattedMessage {...message.content} values={message.values} />,
      notificationType,
      duration: NOTIFICATION_DURATIONS.MEDIUM,
    });
  }
};

export const createBatch = (action$) => {
  return action$.pipe(
    ofType(Actions.CREATE_BATCH_REQUEST),
    mergeMap((action) => {
      const { name, note, assignToUserId } = action;
      return Api.createBatch(name, note, assignToUserId).pipe(
        mergeMap((response) => {
          return of(Actions.createBatchSuccess(response.batchId));
        }),
        catchError((e) => of(Actions.createBatchFailure(e))),
      );
    }),
  );
};

export const createBatchWithSalesOrdersRequest = (action$) =>
  action$.pipe(
    ofType(Actions.CREATE_BATCH_WITH_SALES_ORDERS_REQUEST),
    mergeMap((action) => {
      const { name, assignToUserId, salesOrderIds, filters } = action;
      const undefinedNote = undefined;
      return Api.createBatch(name, undefinedNote, assignToUserId).pipe(
        mergeMap(({ fulfillmentPlanSetId }) => {
          return of(Actions.createBatchWithSalesOrdersSuccess(fulfillmentPlanSetId, salesOrderIds, filters));
        }),
        catchError(() => {
          NotificationService.showNotification({
            title: <FormattedMessage {...messages.createBatchTitle} />,
            content: <FormattedMessage {...messages.createBatchErrorMessage} />,
            notificationType: NOTIFICATION_TYPE_ERROR,
            dontHide: false,
            progress: false,
          });
        }),
      );
    }),
  );

export const addFulfillmentPlanOrSalesOrderToBatchRequest = (action$) =>
  action$.pipe(
    ofType(Actions.ADD_FULFILLMENT_PLANS_OR_SALES_ORDERS_TO_BATCH_REQUEST),
    mergeMap(({ fulfillmentPlanSetId, fulfillmentPlanIds, filters }) => {
      if (fulfillmentPlanIds.length || filters) {
        return Api.addFulfillmentPlansToBatch(fulfillmentPlanSetId, fulfillmentPlanIds, filters).pipe(
          mergeMap((response) => {
            const fulfillmentPlansInOtherBatches = get(response, 'data.fulfillmentPlansInOtherBatches');
            if (get(fulfillmentPlansInOtherBatches, 'length', 0) > 0) {
              return of(
                Actions.fulfillmentPlansBlockedFromBatch(fulfillmentPlansInOtherBatches, fulfillmentPlanSetId),
                Actions.addedFulfillmentPlansToBatchSuccess(),
              );
            }
            return of(Actions.addedFulfillmentPlansToBatchSuccess(), bulkloadFulfillmentPlans(fulfillmentPlanIds));
          }),
          catchError(() => {
            return EMPTY;
          }),
        );
      }

      return EMPTY;
    }),
  );

export const saveBatchesEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.SAVE_BATCHES_REQUEST),
    mergeMap((action) => {
      const { batches } = action;
      const saveBatchesRequests = batches.map((batch) =>
        Api.saveBatch(batch.fulfillmentPlanSetId, batch.name, batch.note, batch.assignedToGuid),
      );
      return forkJoin(...saveBatchesRequests).pipe(
        mergeMap(() => {
          return of(BatchesGridActions.getBatchRows());
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

const showCancelBatchSuccessNotification = (firstBatch, batchIds) => {
  const notificationContent =
    batchIds.length > 1 ? (
      <FormattedMessage
        {...messages.cancelBatchSuccessBodyMultiple}
        values={{
          batchCount: <FormattedNumber value={batchIds.length} />,
        }}
      />
    ) : (
      <FormattedMessage
        {...messages.cancelBatchSuccessBodySingular}
        values={{
          batchName: firstBatch.fulfillmentPlanSetName,
        }}
      />
    );
  NotificationService.showNotification({
    title: <FormattedMessage {...messages.cancelBatchSuccessTitle} />,
    content: notificationContent,
    notificationType: NOTIFICATION_TYPE_SUCCESS,
    duration: 6000,
  });
};

export const cancelBatchEpic = (action$, { getState }, { push }) => {
  return action$.pipe(
    ofType(Actions.CANCEL_BATCH_REQUEST),
    mergeMap((action) => {
      const { batchIds, isDetails } = action;
      const isOnOrdersGrid = window.location.pathname.includes('/orders/');
      const pathname = isOnOrdersGrid ? DEFAULT_ORDERS_PAGE_URL : '/shipments/batches/open';
      const firstBatch = Global.selectEntitiesFulfillmentPlanSetById(getState(), { fulfillmentPlanSetId: batchIds[0] });
      return Api.cancelBatch(batchIds).pipe(
        mergeMap(() => {
          // Success case - simplified API returns void on success
          showCancelBatchSuccessNotification(firstBatch, batchIds);
          if (isDetails && isOnOrdersGrid) {
            push({ pathname });
            return of(Actions.clearBatch(batchIds));
          }
          if (isDetails) {
            push({ pathname });
            return EMPTY;
          }
          return of(Actions.clearBatch(batchIds));
        }),
        catchError(() => {
          NotificationService.showNotification({
            title: <FormattedMessage {...messages.cancelBatchErrorTitle} />,
            content: <FormattedMessage {...messages.cancelBatchErrorBody} />,
            notificationType: NOTIFICATION_TYPE_ERROR,
            duration: 6000,
          });
          return EMPTY;
        }),
      );
    }),
  );
};

export const saveBatchNameChange = (action$) => {
  return action$.pipe(
    ofType(Actions.SAVE_BATCH_NAME_CHANGE_REQUEST),
    mergeMap((action) => {
      const { batch } = action;
      return Api.saveBatch(batch.fulfillmentPlanSetId, batch.name, batch.note, batch.assignedToGuid).pipe(
        mergeMap(() => {
          // NOTE: SPD-9235 - This is a ephemeral piece of logic that patches fulfillmentPlanSets within entities until we consolidate the logic to use entities.
          const fulfillmentPlanSetEntities = [
            {
              fulfillmentPlanSetId: batch.fulfillmentPlanSetId,
              fulfillmentPlanSetName: batch.name,
            },
          ];
          return concat(
            of(Actions.saveBatchNameChangeSuccess(batch)),
            of(
              EntitiesActions.entitiesBulkload({
                fulfillmentPlanSets: createLookupHash(fulfillmentPlanSetEntities, 'fulfillmentPlanSetId'),
              }),
            ),
            of(NavActions.updateBatchNavName(batch.fulfillmentPlanSetId, batch.name)),
          );
        }),
        catchError((e) => of(Actions.saveBatchNameChangeFailure(e))),
      );
    }),
  );
};

export const createBatchWithFulfillmentPlanRequest = (action$) =>
  action$.pipe(
    ofType(Actions.CREATE_BATCH_WITH_FULFILLMENT_PLANS_REQUEST),
    mergeMap((action) => {
      const { name, assignToUserId, fulfillmentPlanIds, shouldForceMoveFulfillmentPlans, filters } = action;
      const undefinedNote = undefined;
      return Api.createBatch(name, undefinedNote, assignToUserId).pipe(
        mergeMap(({ fulfillmentPlanSetId }) => {
          return of(
            Actions.createBatchWithFulfillmentPlansSuccess(
              fulfillmentPlanSetId,
              fulfillmentPlanIds,
              shouldForceMoveFulfillmentPlans,
              filters,
            ),
          );
        }),
        catchError((e) => {
          return of(Actions.createBatchWithFulfillmentPlansFailure(e));
        }),
      );
    }),
  );

export const onCreateBatchWithSalesOrdersRequestSuccess = (action$) => {
  return action$.pipe(
    ofType(Actions.CREATE_BATCH_WITH_SALES_ORDERS_SUCCESS),
    mergeMap(({ fulfillmentPlanSetId, salesOrderIds, filters }) => {
      return Api.addSalesOrdersToBatch(fulfillmentPlanSetId, salesOrderIds, filters).pipe(
        mergeMap(({ data = {} }) => {
          const { fulfillmentPlansInOtherBatches = [] } = data;
          if (fulfillmentPlansInOtherBatches.length) {
            return concat(
              of(Actions.salesOrderFulfillmentPlansBlockedFromBatch(fulfillmentPlansInOtherBatches, fulfillmentPlanSetId)),
              of(Actions.addedSalesOrdersToCreatedBatchSuccess()),
            );
          }
          return concat(
            of(Actions.addedSalesOrdersToCreatedBatchSuccess(), bulkloadSalesOrders(salesOrderIds)),
            of(BatchesGridActions.getBatchRows()),
          );
        }),
        catchError(() => {
          NotificationService.showNotification({
            title: <FormattedMessage {...messages.createBatchTitle} />,
            content: <FormattedMessage {...messages.createBatchErrorMessage} />,
            notificationType: NOTIFICATION_TYPE_ERROR,
            dontHide: false,
            progress: false,
          });

          return EMPTY;
        }),
      );
    }),
  );
};

export const onCreateBatchWithFulfillmentPlansRequestSuccess = (action$) =>
  action$.pipe(
    ofType(Actions.CREATE_BATCH_WITH_FULFILLMENT_PLANS_SUCCESS),
    mergeMap(({ fulfillmentPlanSetId, fulfillmentPlanIds, shouldForceMoveFulfillmentPlans, filters }) => {
      if (shouldForceMoveFulfillmentPlans) {
        return of(Actions.forceAddToBatch(fulfillmentPlanSetId, fulfillmentPlanIds));
      }

      return Api.addFulfillmentPlansToBatch(fulfillmentPlanSetId, fulfillmentPlanIds, filters).pipe(
        mergeMap(({ data = {} }) => {
          const { fulfillmentPlansInOtherBatches = [] } = data;
          if (fulfillmentPlansInOtherBatches.length) {
            return of(
              Actions.fulfillmentPlansBlockedFromBatch(fulfillmentPlansInOtherBatches, fulfillmentPlanSetId),
              Actions.addedFulfillmentPlansToCreatedBatchSuccess(fulfillmentPlanSetId),
            );
          }
          const actions = [Actions.addedFulfillmentPlansToCreatedBatchSuccess(fulfillmentPlanSetId)];
          if (fulfillmentPlanIds && fulfillmentPlanIds.length) {
            actions.push(bulkloadFulfillmentPlans(fulfillmentPlanIds));
          }
          return of(...actions);
        }),
      );
    }),
  );

export const forceAddToBatch = (action$) =>
  action$.pipe(
    ofType(Actions.FORCE_ADD_TO_BATCH),
    mergeMap((action) => {
      const { fulfillmentPlanSetId, fulfillmentPlanIds, meta, errorMessage, successMessage } = action;
      const { onSuccess, onError } = meta;
      if (!fulfillmentPlanSetId || !fulfillmentPlanIds) {
        return EMPTY;
      }
      return BatchDetailsApi.forceAddFulfillmentPlans(fulfillmentPlanSetId, fulfillmentPlanIds).pipe(
        mergeMap(() => {
          showNotificationForOrderDetailsAddToBatch({
            message: successMessage,
            notificationType: NOTIFICATION_TYPE_SUCCESS,
          });
          return of(onSuccess(fulfillmentPlanSetId));
        }),
        catchError((e) => {
          showNotificationForOrderDetailsAddToBatch({
            message: errorMessage,
            notificationType: NOTIFICATION_TYPE_ERROR,
          });
          return of(onError(e));
        }),
      );
    }),
  );

export const onSalesOrderFulfillmentPlansRejectedFromBatchEpic = (action$) =>
  action$.pipe(
    ofType(Actions.SALES_ORDER_FULFILLMENT_PLANS_BLOCKED_FROM_BATCH, Actions.FULFILLMENT_PLANS_BLOCKED_FROM_BATCH),
    mergeMap(({ destinationFulfillmentPlanSetId, type, errorMessage, successMessage }) => {
      return new Observable((observer) => {
        ModalService.launchModal({
          component: BatchRejectedFulfillmentPlanModal,
          props: {
            destinationFulfillmentPlanSetId,
            useFulfillmentPlans: type === Actions.FULFILLMENT_PLANS_BLOCKED_FROM_BATCH,
            errorMessage,
            successMessage,
          },
          onCancel: () => {
            observer.next(of(Actions.cancelBlockedAddToBatch()));
          },
          onClose: () => {
            observer.complete();
          },
        });
      }).pipe(mergeMap((next$) => next$));
    }),
  );
